import { useState, type ReactNode } from 'react';

import { api } from '../api';
import {
  converterDoServidor,
  filtroVazio,
  ListaDeFiltros,
  paraDominio,
  type Filtro,
} from '../filtros';
import { Aviso, Campo, Etiqueta, Painel, useAcao, useCarga } from '../comum';

/**
 * Editor de regra do Sentinela.
 *
 * O desenho segue a definição do domínio, que é o que torna a regra explicável:
 *
 *   **violação = está no escopo E NÃO satisfaz a exigência**
 *
 * Escopo e exigência são a mesma estrutura de filtro — a mesma que o chat
 * produz e que o compilador vira SQL. Por isso a tela pode ser uma só.
 *
 * A pré-visualização é o centro da experiência: ela responde *"quantas máquinas
 * isso pega?"* **sem gravar nada**. Sem ela, criar regra é apostar no parque do
 * cliente — e o primeiro erro custa um alerta em 340 máquinas.
 */


interface Amostra {
  readonly id: string;
  readonly nome: string;
  readonly soNome: string | null;
  readonly viabilidade: string | null;
  readonly resumoViabilidade: string | null;
}

interface Previa {
  readonly noEscopo: number;
  readonly conformes: number;
  readonly violando: number;
  readonly indeterminados: number;
  readonly amostra: readonly Amostra[];
  readonly naoMedidos: readonly { readonly id: string; readonly nome: string }[];
  readonly viabilidade: {
    readonly aptos: number;
    readonly aptosComRessalva: number;
    readonly inaptos: number;
    readonly indeterminados: number;
    readonly lacunasDeColeta: readonly { readonly falta: string; readonly maquinas: number }[];
  } | null;
}

interface Script {
  readonly idNoKace: number;
  readonly nome: string;
  readonly descricao: string | null;
}

export interface RemediacaoInicial {
  readonly scriptId: string;
  readonly tamanhoOnda: string;
  readonly limiteFalhas: string;
}

/**
 * Preenche os campos da remediação a partir da regra que veio do servidor.
 *
 * Separado do componente para poder ser testado: o defeito que ele conserta
 * apagava configuração em silêncio, e um teste é o que impede a volta dele.
 * Os padrões (onda de 5, 3 falhas) são os mesmos de uma regra nova.
 */
export function remediacaoInicial(
  regra: Record<string, unknown> | null,
): RemediacaoInicial {
  const padrao = { scriptId: '', tamanhoOnda: '5', limiteFalhas: '3' };
  const acao = regra?.['acao'];
  if (acao === null || acao === undefined || typeof acao !== 'object') return padrao;

  const a = acao as Record<string, unknown>;
  const texto = (v: unknown, se: string): string =>
    typeof v === 'number' || (typeof v === 'string' && v !== '') ? String(v) : se;

  return {
    scriptId: texto(a['scriptIdNoKace'], ''),
    tamanhoOnda: texto(a['tamanhoDaOnda'], '5'),
    limiteFalhas: texto(a['limiteDeFalhasConsecutivas'], '3'),
  };
}

export function EditorRegra({
  regraExistente,
  podeRemediacao,
  aoFechar,
  aoSalvar,
  aoPerderSessao,
}: {
  regraExistente: Record<string, unknown> | null;
  /**
   * `editar_regra_com_remediacao` — exige `machines` **e** `scripts` em WRITE.
   *
   * 🔴 A checagem tem de estar aqui, não só no botão de salvar. Configurar uma
   * automação que você mesmo não poderia executar à mão é o furo que o mapa de
   * permissões existe para fechar — e descobrir isso depois de preencher o
   * formulário inteiro é a pior forma de descobrir.
   */
  podeRemediacao: boolean;
  aoFechar: () => void;
  aoSalvar: () => void;
  aoPerderSessao: () => void;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const editando = regraExistente !== null;

  const [nome, setNome] = useState(String(regraExistente?.['nome'] ?? ''));
  /**
   * 🔴 O alvo não muda numa edição.
   *
   * Trocar de máquina para chamado deixaria as violações abertas apontando
   * para o alvo errado, e os filtros escritos para um vocabulário seriam
   * avaliados contra outro — sem erro, com resultado sem sentido. O servidor
   * também recusa; aqui o `<select>` fica desabilitado para a pessoa não
   * tentar e perder o trabalho.
   */
  const [alvo, setAlvo] = useState<'dispositivo' | 'chamado'>(
    regraExistente?.['alvo'] === 'chamado' ? 'chamado' : 'dispositivo',
  );
  const [descricao, setDescricao] = useState(String(regraExistente?.['descricao'] ?? ''));
  const [severidade, setSeveridade] = useState(
    String(regraExistente?.['severidade'] ?? 'media'),
  );
  const [preRequisito, setPreRequisito] = useState(
    String(regraExistente?.['preRequisito'] ?? 'nenhum'),
  );
  const [escopo, setEscopo] = useState<Filtro[]>(
    converterDoServidor(regraExistente?.['escopo']),
  );
  const [exigencia, setExigencia] = useState<Filtro[]>(
    converterDoServidor(regraExistente?.['exigencia'], true),
  );
  const [previa, setPrevia] = useState<Previa | null>(null);

  // Remediação (opcional)
  const [remedia, setRemedia] = useState(
    (regraExistente?.['modo'] as string | undefined) === 'remediar',
  );
  /**
   * 🔴 Os campos da remediação vinham em branco ao editar — e salvar apagava.
   *
   * `remedia` nascia `true` para uma regra que remedia, mas `scriptId` nascia
   * vazio, e `corpo()` só monta `modo: remediar` quando há script. Resultado:
   * abrir uma regra que remedia, clicar em Editar, salvar sem tocar em nada, e
   * a remediação sumia — a regra virava observação. Sem erro, sem aviso.
   *
   * Perda silenciosa de configuração é o pior tipo: ninguém percebe até a
   * remediação não rodar no dia em que era para rodar.
   */
  const inicial = remediacaoInicial(regraExistente);
  const [scriptId, setScriptId] = useState(inicial.scriptId);
  const [tamanhoOnda, setTamanhoOnda] = useState(inicial.tamanhoOnda);
  const [limiteFalhas, setLimiteFalhas] = useState(inicial.limiteFalhas);

  const scripts = useCarga<{ itens: readonly Script[] }>(
    () => api.get('/api/kace/scripts'),
    [],
    aoPerderSessao,
  );

  const corpo = (): Record<string, unknown> => ({
    nome,
    alvo,
    descricao,
    severidade,
    // Viabilidade é sobre hardware; o servidor recusa se vier em regra de
    // chamado, e mandar mesmo assim faria a pessoa levar um erro sem causa
    // visível na tela.
    preRequisito: alvo === 'chamado' ? 'nenhum' : preRequisito,
    escopo: escopo.map(paraDominio),
    exigencia: exigencia.map(paraDominio),
    ...(alvo !== 'chamado' && remedia && scriptId !== ''
      ? {
          modo: {
            kind: 'remediar',
            acao: {
              scriptIdNoKace: Number(scriptId),
              tamanhoDaOnda: Number(tamanhoOnda),
              limiteDeFalhasConsecutivas: Number(limiteFalhas),
            },
          },
        }
      : { modo: { kind: 'observar' } }),
  });

  const preVisualizar = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<Previa>('/api/regras/pre-visualizar', {
        alvo,
        escopo: escopo.map(paraDominio),
        exigencia: exigencia.map(paraDominio),
        preRequisito,
      });
      setPrevia(r);
      const unidade = alvo === 'chamado' ? 'chamado(s)' : 'dispositivo(s)';
      return `${r.violando} violação(ões) em ${r.noEscopo} ${unidade} no escopo.`;
    });
  };

  const salvar = (): void => {
    void acao.rodar(async () => {
      if (editando) {
        await api.put(`/api/regras/${String(regraExistente['id'])}`, corpo());
      } else {
        await api.post('/api/regras', corpo());
      }
      aoSalvar();
      return editando ? 'Regra atualizada.' : 'Regra criada — e nasce desativada.';
    });
  };

  return (
    <Painel
      titulo={editando ? 'Editar regra' : 'Nova regra'}
      sub="Violação = está no escopo E não satisfaz a exigência."
    >
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      <div className="grade2">
        <Campo rotulo="Nome">
          <input
            value={nome}
            placeholder="Máquinas TALL não podem estar no Windows 10"
            onChange={(e) => setNome(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Severidade">
          <select value={severidade} onChange={(e) => setSeveridade(e.target.value)}>
            <option value="informativa">Informativa</option>
            <option value="baixa">Baixa</option>
            <option value="media">Média</option>
            <option value="alta">Alta</option>
            <option value="critica">Crítica</option>
          </select>
        </Campo>
      </div>

      <Campo
        rotulo="Sobre o que é a regra"
        ajuda={
          editando
            ? 'Não muda depois de criada — os filtros e as violações já estão escritos para este alvo.'
            : 'Máquina usa campos de inventário; chamado usa campos de service desk. Os dois vocabulários não se misturam.'
        }
      >
        <select
          value={alvo}
          disabled={editando}
          onChange={(e) => {
            const novo = e.target.value === 'chamado' ? 'chamado' : 'dispositivo';
            setAlvo(novo);
            // Os filtros do alvo anterior não existem no novo vocabulário.
            setEscopo([]);
            setExigencia([filtroVazio(novo)]);
            setPrevia(null);
          }}
        >
          <option value="dispositivo">Máquinas do parque</option>
          <option value="chamado">Chamados do service desk</option>
        </select>
      </Campo>

      {alvo === 'chamado' && (
        <Aviso tipo="info">
          Regra sobre chamado <strong>observa e alerta</strong>. Não há remediação:
          não existe script do KACE que conserte um chamado — a correção é humana.
        </Aviso>
      )}

      <Campo rotulo="Descrição" ajuda="Aparece no chamado que a regra abre.">
        <input value={descricao} onChange={(e) => setDescricao(e.target.value)} />
      </Campo>

      <ListaDeFiltros
        titulo="Escopo — a quem a regra se aplica"
        ajuda={alvo === 'chamado' ? 'Vazio = todos os chamados.' : 'Vazio = todo o parque.'}
        filtros={escopo}
        aoMudar={setEscopo}
        alvo={alvo}
      />

      <ListaDeFiltros
        titulo="Exigência — o que precisa ser verdade"
        ajuda="Quem está no escopo e NÃO satisfaz isto vira violação. Obrigatório."
        filtros={exigencia}
        aoMudar={setExigencia}
        alvo={alvo}
      />

      {/*
        Viabilidade e remediação existem só para máquina. Escondê-las em regra
        de chamado é melhor que desabilitá-las: campo desabilitado ainda sugere
        que a coisa existe e que falta alguma permissão.
      */}
      {alvo !== 'chamado' && (
        <>
      <Campo
        rotulo="Análise de viabilidade"
        ajuda="Quando marcada, o produto avalia se cada máquina aguenta a correção antes de propor qualquer coisa."
      >
        <select value={preRequisito} onChange={(e) => setPreRequisito(e.target.value)}>
          <option value="nenhum">Nenhuma</option>
          <option value="windows_11">Requisitos do Windows 11</option>
        </select>
      </Campo>

      {/* ── Remediação ─────────────────────────────────────────────────── */}
      <label
        style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 8 }}
        className={podeRemediacao ? undefined : 'suave'}
      >
        <input
          type="checkbox"
          checked={remedia && podeRemediacao}
          disabled={!podeRemediacao}
          onChange={(e) => setRemedia(e.target.checked)}
        />
        <span>
          <strong>Esta regra pode remediar</strong>
          <span className="ajuda">
            {podeRemediacao ? (
              <>
                Executa um script do KACE nas máquinas violando — sempre em ondas, com
                aprovação humana e disjuntor. Nada roda sozinho.
              </>
            ) : (
              <>
                Indisponível para você: remediar executa script nas máquinas, e isso
                exige permissão de escrita em <strong>scripts</strong> no KACE. Ajuste a
                função deste usuário lá — o produto não concede acesso que o console não
                conceda. A regra pode ser salva em modo observação.
              </>
            )}
          </span>
        </span>
      </label>

      {remedia && podeRemediacao && (
        <div className="grade2">
          <Campo
            rotulo="Script do KACE"
            ajuda="Só aparecem scripts em produção e habilitados. Os de fábrica ficam de fora."
          >
            <select value={scriptId} onChange={(e) => setScriptId(e.target.value)}>
              <option value="">Escolha…</option>
              {(scripts.dados?.itens ?? []).map((s) => (
                <option key={s.idNoKace} value={s.idNoKace}>
                  {s.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Tamanho da onda" ajuda="A primeira onda é o canário.">
            <input
              type="number"
              min={1}
              value={tamanhoOnda}
              onChange={(e) => setTamanhoOnda(e.target.value)}
            />
          </Campo>
          <Campo
            rotulo="Disjuntor: falhas consecutivas"
            ajuda="Script quebrado deve custar uma onda, não o parque."
          >
            <input
              type="number"
              min={1}
              value={limiteFalhas}
              onChange={(e) => setLimiteFalhas(e.target.value)}
            />
          </Campo>
        </div>
      )}

      {scripts.erro !== null && remedia && (
        <Aviso tipo="alerta">
          Não foi possível listar os scripts do KACE: {scripts.erro}. A regra pode ser
          salva em modo observação.
        </Aviso>
      )}
        </>
      )}

      <div className="linha" style={{ marginTop: 16 }}>
        <button
          className="encolhe"
          disabled={acao.executando || exigencia.length === 0}
          onClick={preVisualizar}
        >
          Pré-visualizar
        </button>
        <button
          className="primario encolhe"
          disabled={acao.executando || nome.trim() === '' || exigencia.length === 0}
          onClick={salvar}
        >
          {editando ? 'Salvar alterações' : 'Criar regra'}
        </button>
        <button className="encolhe" onClick={aoFechar}>
          Cancelar
        </button>
        <span className="cresce" />
      </div>

      {!editando && (
        <p className="suave" style={{ fontSize: 12.5, marginTop: 10 }}>
          A regra nasce <strong>desativada</strong>. Ligue depois de conferir a
          pré-visualização — é a diferença entre um alerta e 340.
        </p>
      )}

      {previa !== null && <Previsualizacao previa={previa} />}
    </Painel>
  );
}

/** Converte o que veio do servidor para a forma editável da tela. */
/**
 * Resultado da pré-visualização.
 *
 * Mostra os **três** estados, não dois. "Indeterminado" tem coluna própria
 * porque máquina sem dado coletado não é máquina conforme nem violando — e
 * escondê-la faria a regra parecer mais precisa do que é.
 */
function Previsualizacao({ previa }: { previa: Previa }): ReactNode {
  return (
    <div className="previa">
      <div className="cartoes">
        <div className="cartao">
          <div className="rotulo">No escopo</div>
          <div className="valor">{previa.noEscopo}</div>
        </div>
        <div className="cartao">
          <div className="rotulo">Conformes</div>
          <div className="valor">{previa.conformes}</div>
        </div>
        <div className={`cartao${previa.violando > 0 ? ' acende-alerta' : ''}`}>
          <div className="rotulo">Violando</div>
          <div className="valor">{previa.violando}</div>
        </div>
        <div className="cartao">
          <div className="rotulo">Sem dados</div>
          <div className="valor">{previa.indeterminados}</div>
          <div className="nota">não dá para julgar</div>
        </div>
      </div>

      {previa.viabilidade !== null && (
        <Aviso>
          <strong>Viabilidade da correção:</strong> {previa.viabilidade.aptos} aptas ·{' '}
          {previa.viabilidade.aptosComRessalva} com ressalva ·{' '}
          {previa.viabilidade.inaptos} inaptas por hardware ·{' '}
          {previa.viabilidade.indeterminados} sem dados.
          {previa.viabilidade.lacunasDeColeta.length > 0 && (
            <div style={{ marginTop: 8, fontWeight: 400 }}>
              Falta coletar:{' '}
              {previa.viabilidade.lacunasDeColeta
                .slice(0, 4)
                .map((l) => `${l.falta} (${l.maquinas})`)
                .join(' · ')}
            </div>
          )}
        </Aviso>
      )}

      {previa.amostra.length > 0 && (
        <>
          <p className="suave contagem">
            Amostra do que seria marcado como violação — confira se é o que você
            imaginou:
          </p>
          <div className="rolagem">
            <table className="densa">
              <thead>
                <tr>
                  <th>Dispositivo</th>
                  <th>Sistema</th>
                  <th>Viabilidade</th>
                </tr>
              </thead>
              <tbody>
                {previa.amostra.map((a) => (
                  <tr key={a.id}>
                    <td className="forte">{a.nome}</td>
                    <td className="suave">{a.soNome ?? '—'}</td>
                    <td>
                      {a.viabilidade === null ? (
                        <span className="suave">—</span>
                      ) : (
                        <Etiqueta
                          tom={
                            a.viabilidade === 'apto'
                              ? 'ok'
                              : a.viabilidade === 'inapto'
                                ? 'erro'
                                : 'alerta'
                          }
                          {...(a.resumoViabilidade === null
                            ? {}
                            : { titulo: a.resumoViabilidade })}
                        >
                          {a.viabilidade.replace(/_/g, ' ')}
                        </Etiqueta>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {previa.naoMedidos.length > 0 && (
        <p className="suave" style={{ fontSize: 12.5, marginTop: 10 }}>
          {previa.naoMedidos.length} máquina(s) sem dado suficiente para julgar:{' '}
          {previa.naoMedidos.slice(0, 6).map((n) => n.nome).join(', ')}
          {previa.naoMedidos.length > 6 && '…'}
        </p>
      )}
    </div>
  );
}
